import React, { createContext, useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import imageMap, { galleries } from "../components/assets/imageMap";

const resolveImages = (data) => {
    if (data.gallery) return galleries[data.gallery] || [];
    if (data.imageKey) return [imageMap[data.imageKey]];
    return [];
};

export const ShopContext = createContext(null);

// Shared list of variant attributes - used to build both the human-readable
// cart label (" · "-joined) and the "~"-delimited WhatsApp order message.
const variantParts = (variant) => {
    if (!variant) return [];
    const parts = [
        variant.eggType === "egg" ? "Egg" : "Eggless",
        variant.format === "full" ? "Full Cake" : "Boxed Slice",
    ];
    if (variant.sheetSize) {
        parts.push("Sheet Cake", variant.sheetSize === "quarter" ? "Quarter Sheet" : "Half Sheet");
    } else if (variant.format === "full") {
        parts.push("Round Cake");
        if (variant.lbs) parts.push(`${variant.lbs} lb`);
    }
    if (variant.layers) parts.push(`${variant.layers} Layers`);
    if (variant.pieces) parts.push(`${variant.pieces} pcs`);
    return parts;
};

const variantLabel = (variant) => {
    const parts = variantParts(variant);
    return parts.length > 0 ? parts.join(" · ") : null;
};

const cartKeyFor = (productId, variant) => {
    return variant
        ? `${productId}|${variant.eggType}|${variant.format}|${variant.layers || 1}|${variant.pieces || ""}|${variant.lbs || ""}|${variant.sheetSize || ""}`
        : `${productId}`;
};

const ShopContextProvider = (props) => {
    const [all_product, setAllProduct] = useState([]);
    const [cartItems, setCartItems] = useState({});

    // CartDetails holds the string value of the order for Whatsapp
    const [orderDetails, setOrderDetails,] = useState("");

    const [deliveryCharge, setDeliveryCharge,] = useState(0.00);

    useEffect(() => {
        const fetchProducts = async () => {
            const snapshot = await getDocs(collection(db, "products"));
            const products = snapshot.docs.map((doc) => {
                const data = doc.data();
                const images = resolveImages(data);
                return { ...data, images, image: images[0] };
            });
            setAllProduct(products);
        };
        fetchProducts();
    }, []);

    const addToCart = (product, variant = null) => {
        const cartKey = cartKeyFor(product.id, variant);
        const price = variant ? variant.price : product.new_price;
        setCartItems((prev) => ({
            ...prev,
            [cartKey]: {
                productId: product.id,
                name: product.name,
                image: product.image,
                price,
                variantLabel: variantLabel(variant),
                variantParts: variantParts(variant),
                quantity: (prev[cartKey]?.quantity || 0) + 1,
            },
        }));
    };

    const removeFromCart = (cartKey) => {
        setCartItems((prev) => {
            const existing = prev[cartKey];
            if (!existing) return prev;
            if (existing.quantity <= 1) {
                const rest = { ...prev };
                delete rest[cartKey];
                return rest;
            }
            return { ...prev, [cartKey]: { ...existing, quantity: existing.quantity - 1 } };
        });
    };

    useEffect(() => {
        // title~egg/eggless~format~cake style~weight~layers~pieces~quantity~price~
        // - one "~"-delimited block per cart item, consolidated into a single message.
        let cartDetails = "";
        Object.values(cartItems).forEach((item) => {
            const fields = [item.name, ...(item.variantParts || []), item.quantity, item.price];
            cartDetails += fields.join("~") + "~";
        });
        setOrderDetails(cartDetails + "delivery~" + deliveryCharge);
    }, [cartItems, deliveryCharge]);

    const getTotalCartAmount = () => {
        return Object.values(cartItems).reduce((total, item) => total + item.price * item.quantity, 0);
    }

    const getTotalAmount = () => {
        return getTotalCartAmount() + deliveryCharge;
    }

    const getTotalCartItems = () => {
        return Object.values(cartItems).reduce((total, item) => total + item.quantity, 0);
    }

    const updateDeliveryCharge = (value) => {
        setDeliveryCharge(value);
    }

    const contextValue = {
        getTotalCartAmount,
        getTotalAmount,
        getTotalCartItems,
        updateDeliveryCharge,
        all_product,
        cartItems,
        addToCart,
        removeFromCart,
        orderDetails
    };


    return (
        <ShopContext.Provider value={contextValue}>
            {props.children}
        </ShopContext.Provider>
    )
};

export default ShopContextProvider;
